import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { LogOut, Menu, X } from 'lucide-react';
import { Navbar } from './Navbar';
import { Link, useNavigate } from 'react-router-dom';
import { BottomNav } from './BottomNav';

interface SidebarItem {
  icon: any;
  label: string;
  id: string;
  count?: number; // Optional badge count
  onClick?: () => void; // Optional custom click handler
}

export const DashboardLayout: React.FC<{
  children: React.ReactNode;
  title: string;
  sidebarItems: SidebarItem[];
  activeTab: string;
  onTabChange: (id: string) => void;
}> = ({ children, title, sidebarItems, activeTab, onTabChange }) => {
  const { user, logout } = useAuth();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Common Sidebar Content Component
  const SidebarContent = () => (
    <>
      <div className="h-20 flex items-center px-6 border-b border-gray-200 bg-white">
        <img src="/logo.png" alt="Indigo Beans" className="h-10 w-auto" />
      </div>

      <div className="flex-1 overflow-y-auto py-6 bg-white">
        <nav className="px-3 space-y-1">
          {sidebarItems.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                if (item.onClick) {
                  item.onClick();
                } else {
                  onTabChange(item.id);
                }
                setIsMobileSidebarOpen(false); // Close mobile menu on selection
              }}
              className={`w-full flex items-center justify-between px-3 py-3 text-sm font-medium rounded-md transition-colors ${activeTab === item.id
                ? 'bg-[#2f5c96]/5 text-[#2f5c96]'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
            >
              <div className="flex items-center">
                <item.icon className={`mr-3 h-5 w-5 ${activeTab === item.id ? 'text-[#2f5c96]' : 'text-gray-400'}`} />
                {item.label}
              </div>
              {item.count !== undefined && item.count > 0 && (
                <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {item.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <Link to="/profile" className="flex items-center gap-3 mb-3 hover:bg-gray-100 p-2 rounded-lg transition-colors group">
          <img src={user?.avatar || `https://ui-avatars.com/api/?name=${user?.name}`} alt="" className="w-10 h-10 rounded-full bg-gray-300 border border-white shadow-sm object-cover" />
          <div className="overflow-hidden">
            <p className="text-sm font-bold text-gray-800 truncate group-hover:text-[#2f5c96]">{user?.name}</p>
            <p className="text-xs text-gray-500 truncate capitalize">{user?.role.toLowerCase()}</p>
          </div>
        </Link>
        <button onClick={handleLogout} className="w-full flex items-center px-2 py-2 text-sm text-red-600 rounded-md hover:bg-red-50 transition-colors">
          <LogOut className="mr-3 h-4 w-4" />
          Sign Out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Global Navbar */}
      <Navbar />

      <div className="flex flex-1 relative">
        {/* Sidebar - Desktop */}
        <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-gray-200 fixed h-[calc(100vh-80px)] z-10 bottom-0 top-20">
          <SidebarContent />
        </aside>

        {/* Sidebar - Mobile (Slide-in Drawer) */}
        {isMobileSidebarOpen && (
          <div className="relative z-50 lg:hidden" role="dialog" aria-modal="true">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity"
              onClick={() => setIsMobileSidebarOpen(false)}
            />

            {/* Drawer */}
            <div className="fixed inset-y-0 left-0 z-50 w-64 overflow-y-auto bg-white shadow-xl transition-transform duration-300 ease-in-out transform translate-x-0 flex flex-col h-full">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  onClick={() => setIsMobileSidebarOpen(false)}
                  className="flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                >
                  <X className="h-6 w-6 text-gray-400" />
                </button>
              </div>
              <SidebarContent />
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 lg:ml-64 flex flex-col min-h-[calc(100vh-80px)] pb-16 lg:pb-0">
          <header className="bg-white shadow-sm lg:hidden h-16 flex items-center px-4 justify-between sticky top-0 z-20">
            <div className="flex items-center gap-2">
              <span className="font-bold text-lg text-[#2f5c96]">{title}</span>
            </div>
            <button
              onClick={() => setIsMobileSidebarOpen(true)}
              className="-m-2.5 p-2.5 text-gray-700 hover:bg-gray-50 rounded-md"
            >
              <span className="sr-only">Open sidebar</span>
              <Menu className="h-6 w-6" />
            </button>
          </header>

          <main className="flex-1 p-4 sm:p-8">
            <div className="w-full">
              <div className="hidden lg:block mb-8">
                <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
              </div>
              {children}
            </div>
          </main>
        </div>
      </div>

      {/* Bottom Nav for Mobile Dashboards */}
      <BottomNav />
    </div>
  );
};